import { useState, useMemo } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Search, Plus } from "lucide-react";
import { SECTION_KINDS, SECTION_LABELS } from "@/components/sections/constants";

// A short one-liner shown under each block in the palette so users know what
// they're adding without needing to render a preview. Keep it short — the
// card is 240px wide.
const HINTS = {
  hero: "Big headline · subtitle · CTA · image",
  features: "Icon grid with title + description",
  testimonials: "Quotes from happy customers",
  stats: "Number + label rows (proof)",
  cta: "Bold call-to-action band",
  faq: "Collapsible Q&A list",
  about: "Long-form founder / brand story",
  pricing: "Plan cards with perks",
  gallery: "Image grid",
  waitlist: "Email capture form",
  registration_form: "Name · phone · email · city → CRM lead",
  countdown: "Live timer to a target date",
  youtube_video: "Embed any YouTube video",
  instagram_embed: "Link to your Instagram",
  video_embed: "Self-hosted MP4 with poster",
  button: "Standalone CTA button",
  logos_strip: "Featured-in style logo row",
  contact_info: "WhatsApp · phone · email · address",
  steps: "Numbered how-it-works",
  image_text: "Image + copy, either side",
  text_block: "Rich paragraph block",
  custom_code: "Sandboxed HTML/CSS/JS",
};

function PaletteItem({ kind, onAdd }) {
  // Draggable so users can drop it at a specific canvas position.
  // Also keeps a click-to-append affordance via the + button on the right.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${kind}`,
    data: { kind, source: "palette" },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      className={"group relative rounded-xl border border-[#E4E5E9] bg-white p-3 cursor-grab active:cursor-grabbing hover:border-[#0B5CFF] hover:shadow-md transition-all " + (isDragging ? "opacity-40" : "")}
      data-testid={`palette-item-${kind}`}
    >
      <div {...listeners} className="pr-8">
        <div className="font-semibold text-sm text-[#0B0D17]">{SECTION_LABELS[kind]}</div>
        <div className="text-[11px] text-[#747680] mt-0.5 leading-tight line-clamp-2">{HINTS[kind]}</div>
      </div>
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onAdd(kind); }}
        className="absolute right-2 top-2 w-7 h-7 rounded-lg bg-[#0B5CFF]/10 text-[#0B5CFF] hover:bg-[#0B5CFF] hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        title="Add to end of page"
        data-testid={`palette-add-${kind}`}
      >
        <Plus className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}

export function BlockPalette({ onAdd, onOpenLibrary, onOpenTemplates, snippetCount = 0 }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return SECTION_KINDS;
    return SECTION_KINDS.filter((k) =>
      (SECTION_LABELS[k] || k).toLowerCase().includes(query) ||
      (HINTS[k] || "").toLowerCase().includes(query)
    );
  }, [q]);

  return (
    <aside className="w-64 shrink-0 border-r border-[#E4E5E9] bg-[#F9FAFB] flex flex-col" data-testid="block-palette">
      <div className="p-4 border-b border-[#E4E5E9]">
        <div className="vp-label mb-2" style={{ fontSize: "0.65rem" }}>Blocks</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-[#747680] absolute left-2.5 top-2.5" strokeWidth={1.75} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search blocks…"
            className="w-full h-8 pl-8 pr-2 rounded-lg border border-[#E4E5E9] text-xs bg-white focus:border-[#0B5CFF] outline-none"
            data-testid="palette-search"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filtered.map((k) => (
          <PaletteItem key={k} kind={k} onAdd={onAdd} />
        ))}
        {filtered.length === 0 && (
          <div className="text-xs text-[#747680] text-center py-6">No blocks match "{q}".</div>
        )}
      </div>

      <div className="p-3 border-t border-[#E4E5E9] space-y-2">
        <button
          type="button"
          onClick={onOpenTemplates}
          className="w-full text-xs font-semibold py-2 rounded-lg bg-gradient-to-r from-[#0B5CFF] to-[#6D28D9] text-white hover:opacity-90"
          data-testid="palette-open-templates"
        >
          ✨ Templates
        </button>
        <button
          type="button"
          onClick={onOpenLibrary}
          className="w-full text-xs font-semibold py-2 rounded-lg bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-[#0B0D17]"
          data-testid="palette-open-library"
        >
          📚 Library · {snippetCount}
        </button>
      </div>
    </aside>
  );
}
